import { useParams, useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { articles, articleContents } from '@/mocks/articles';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import JsonLd from '@/components/base/JsonLd';
import { SITE_URL } from '@/constants/site';

const siteUrl = SITE_URL;

function buildArticleSchema(article: typeof articleContents[string]) {
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Article",
        "headline": article.title,
        "description": article.subtitle,
        "image": article.image,
        "author": {
          "@type": "Organization",
          "name": "Itaobuy Spread Italia",
          "url": siteUrl
        },
        "publisher": {
          "@type": "Organization",
          "name": "Itaobuy Spread Italia",
          "logo": {
            "@type": "ImageObject",
            "url": "https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/62f99c0415358bd5388d5ed1df9ac1ff.jpeg"
          }
        },
        "datePublished": "2026-07-14",
        "dateModified": "2026-07-14",
        "mainEntityOfPage": {
          "@type": "WebPage",
          "@id": siteUrl + "/articoli/" + article.slug
        },
        "inLanguage": "it"
      },
      {
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": siteUrl
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": article.title,
            "item": siteUrl + "/articoli/" + article.slug
          }
        ]
      }
    ]
  };
}

function ArticleContent({ content }: { content: typeof articleContents[string]['content'] }) {
  const navigate = useNavigate();

  return (
    <div className="prose-custom">
      {content.map((block, idx) => {
        switch (block.type) {
          case 'paragraph':
            return (
              <p key={idx} className="text-foreground-600 leading-relaxed mb-6 text-base">
                {block.text}
              </p>
            );

          case 'heading':
            return (
              <h3 key={idx} className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-12 mb-5">
                {block.text}
              </h3>
            );

          case 'list':
            return (
              <ol key={idx} className="space-y-3 mb-6 pl-5">
                {block.items?.map((item, i) => (
                  <li key={i} className="flex gap-3 text-base text-foreground-600 leading-relaxed">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center text-xs font-bold mt-0.5">
                      {String(i + 1).padStart(2, '0')}
                    </span>
                    <span>{item}</span>
                  </li>
                ))}
              </ol>
            );

          case 'table':
            return (
              <div key={idx} className="overflow-x-auto mb-8 rounded-xl border border-background-200">
                <table className="w-full text-left">
                  <thead>
                    <tr className="bg-background-100">
                      {block.headers?.map((h, i) => (
                        <th key={i} className="py-3.5 px-5 text-sm font-semibold text-foreground-800">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {block.rows?.map((row, ri) => (
                      <tr key={ri} className="border-t border-background-200">
                        {row.map((cell, ci) => (
                          <td key={ci} className={`py-3.5 px-5 text-sm ${ci === 0 ? 'font-medium text-foreground-800' : 'text-foreground-600'}`}>
                            {cell}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );

          case 'faq':
            return (
              <div key={idx} className="space-y-4 mb-8">
                {block.items?.map((faq, i) => (
                  <details key={i} className="group rounded-xl border border-background-200 bg-background-50 overflow-hidden">
                    <summary className="flex items-center justify-between px-6 py-4 cursor-pointer hover:bg-background-100 transition-colors">
                      <span className="text-sm font-semibold text-foreground-800 pr-4">{faq.q}</span>
                      <i className="ri-add-line text-foreground-400 group-open:hidden flex-shrink-0"></i>
                      <i className="ri-subtract-line text-foreground-400 hidden group-open:block flex-shrink-0"></i>
                    </summary>
                    <div className="px-6 pb-4">
                      <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                    </div>
                  </details>
                ))}
              </div>
            );

          case 'cta':
            return (
              <div key={idx} className="my-12 p-8 rounded-2xl bg-primary-50 border border-primary-200 text-center">
                <p className="text-foreground-700 text-base mb-5">{block.text}</p>
                <a
                  href={block.buttonUrl || '#'}
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
                >
                  {block.buttonText}
                  <i className="ri-arrow-right-up-line"></i>
                </a>
              </div>
            );

          default:
            return null;
        }
      })}

      <div className="mt-16 pt-10 border-t border-background-200">
        <nav className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center gap-2 text-sm font-medium text-primary-500 hover:text-primary-600 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i>
            Torna alla Home
          </button>
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-xs text-foreground-400">Altri articoli:</span>
            {articles.filter(a => a.slug !== (typeof window !== 'undefined' ? window.location.pathname.split('/').pop() : '')).slice(0, 4).map((a) => (
              <button
                key={a.id}
                onClick={() => { window.scrollTo(0, 0); navigate(`/articoli/${a.slug}`); }}
                className="text-xs font-medium text-foreground-600 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer"
              >
                {a.category}
              </button>
            ))}
          </div>
        </nav>
      </div>
    </div>
  );
}

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const article = slug ? articleContents[slug] : undefined;

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-32 pb-16">
          <div className="w-full px-4 md:px-8 lg:px-16 text-center">
            <h1 className="font-heading text-3xl font-bold text-foreground-900 mb-4">Articolo non trovato</h1>
            <p className="text-foreground-500 mb-8">L'articolo che stai cercando non è disponibile.</p>
            <button
              onClick={() => navigate('/')}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              Torna alla Home
            </button>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <JsonLd data={buildArticleSchema(article)} />
      <Navbar />
      <main>
        <article className="pt-24 pb-16">
          <div className="w-full px-4 md:px-8 lg:px-16">
            <div className="max-w-3xl mx-auto">
              <nav className="flex items-center gap-2 text-sm text-foreground-400 mb-8">
                <button onClick={() => navigate('/')} className="hover:text-primary-500 transition-colors cursor-pointer">Home</button>
                <i className="ri-arrow-right-s-line text-xs"></i>
                <span className="text-foreground-600">{article.category}</span>
              </nav>

              <span className="inline-block px-3 py-1 rounded-full bg-primary-50 text-primary-600 text-xs font-medium mb-5">
                {article.category}
              </span>

              <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-tight mb-4">
                {article.title}
              </h1>

              <p className="text-foreground-500 text-base md:text-lg leading-relaxed mb-6">
                {article.subtitle}
              </p>

              <div className="flex flex-wrap items-center gap-4 text-xs text-foreground-400 mb-10">
                <span className="flex items-center gap-1.5">
                  <i className="ri-user-line"></i> {article.author}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-calendar-line"></i> {article.date}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-time-line"></i> {article.readTime}
                </span>
              </div>

              <div className="relative w-full aspect-[16/9] rounded-2xl overflow-hidden mb-12">
                <img
                  src={article.image}
                  alt={article.title}
                  title={article.title}
                  className="w-full h-full object-cover"
                />
              </div>

              <ArticleContent content={article.content} />
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}